import { apiToken } from '@/composables/auth'
import { callApi } from '@/services/call-api'
import { Beneficiary } from '@/types/beneficiary.ts'
import type { Snapshot } from '@/types/snapshot.ts'
import { LOCK_DURATION_ID } from '@/types/staking.ts'
import { RAFFLE_ERROR_CODE } from '@/types/shared/error-codes.ts'
import type { GachaAsteroidPrize } from '@/types/shared/gacha-game.ts'
import type { Activity } from '@/types/shared/activity'
import { ACTIVITY_TYPE } from '@/types/shared/activity'
import type { LockConfig } from '@/types/lock-config'
import type { ReferralDataResponse } from '@/types/referral-data'
import { PRIZE_TYPE } from '@/types/shared/raffle-prize.ts'
import type { BADGE_ID } from '@/types/shared/badge.ts'

export enum RAFFLE_PRIZE_TYPE {
  VOUCHERS = 'vouchers',
  PHYSICAL = 'physical',
  NONE = 'none',
}

export enum RAFFLE_TAG {
  HIGHLIGHTED = 'highlighted',
}

export type RaffleWonPrize =
  | { type: RAFFLE_PRIZE_TYPE.PHYSICAL; name: string }
  | { type: RAFFLE_PRIZE_TYPE.VOUCHERS; code: string; amount: number; id: number }
  | { type: RAFFLE_PRIZE_TYPE.NONE }

export type Raffle = {
  id: string
  title: string
  description: string
  imageUrl: string
  startDate: string
  endDate: string
  ticketPrice: number
  maxTicketsPerUser: number | null
  prizes: {
    name: string
    description: string
    imageUrl: string
    count: number
    type: RAFFLE_PRIZE_TYPE
    id: number,
    isBigPrice: boolean
  }[]
  metadata: {
    ticketsCount: number
    isConcluded: boolean
    tags: RAFFLE_TAG
    participationQuestions: { question: string }[]
    extraTickets: {
      onStake?: {
        minAmount: number
        canRepeat: boolean
        ticketsCountByDuration: Partial<Record<LOCK_DURATION_ID, number>>
      }
    }
    participantsCount: number
    web3Seed: {
      transactionHash: string
      value: string
    } | null
  }
  hasElement: boolean
}

export type RaffleStatus = {
  ticketsCount: number
  extraTicketsCount: number
  wonPrizeV2: RaffleWonPrize | null
  submission: { email: string } | null
  prizeSubmittedData: Record<string, string> | null
  raffleSubmittedData: Record<string, string> | null
}

export type Stake = {
  id: string
  amount: string
  lingoPrice: string
  timestamp: number
  lockDurationId: LOCK_DURATION_ID
  unlockBlock: number
  unlockTimestamp: number
  unlockDurationBlocks: number
  boost: number
  lockPowerBoost: number
  dailyPower: number
  dailyPowerWithBoost: number
  baseDailyPower: number
  description: string
  imageUrl: string
}

export type RecentClaim = {
  prizeName: string
  prizeImage: string
  walletAddress: string
  timestamp: number
}

export type ExclusiveEvent = {
  id: string
  name: string
  startDate: string
  endDate: string
  description: string
  imageUrl: string
  quantity: number
  claimed: number
  minLevel: number
  minAmountStaked: number
  minLockDurationBlocks: number
}

export type ExclusiveEventClaim = {
  walletAddress: string
  eventId: string,
  timestamp: string
}

export type Prize =
  | { type: PRIZE_TYPE.PHYSICAL, name: string }
  | { type: PRIZE_TYPE.VOUCHERS, amount: number }

export type Winner = {
  walletAddress: string
  prize: Prize
}

type WinnersResponse = {
  winners: Winner[]
}

export class ApiError extends Error {
  constructor(message: string, public readonly metadata?: Record<string, unknown>) {
    super(message)
  }
}

export class Api {
  private async fetch<T>(input: { path: string; body?: object; method: 'POST' | 'DELETE' | 'GET' }): Promise<T> {
    return await callApi({
      ...input,
      path: `/api${input.path}`,
      headers: {
        Authorization: `Bearer ${apiToken.value}`,
      },
    })
  }

  public async getMerkleProof(beneficiary: Beneficiary) {
    return await this.fetch<{ proof: string[] }>({
      path: '/staking/merkle-proof',
      method: 'POST',
      body: {
        beneficiary,
      },
    })
  }

  public async getStakes() {
    return await this.fetch<{
      stakes: Stake[]
      powers: { total: string } & Record<string, string>
      referral: {
        count: number
        power: string
      }
      powerConfig: {
        oneLingoBaseDailyPower: number
        manualBoost: { amount: number; expiresAt: string } | null
        lastPowerMileGenerationTimestamp: number
      }
    }>({
      path: '/staking/stakes',
      method: 'POST',
    })
  }

  public async submitRafflePrizeData({ raffleId, email }: { raffleId: string; email?: string }) {
    return await this.fetch<{ prize: RaffleWonPrize | null }>({
      path: `/raffles/${raffleId}/submit-prize-data`,
      method: 'POST',
      body: { email },
    })
  }

  public async getUserStatusForRaffles() {
    return await this.fetch<{
      me: Record<string, RaffleStatus>
    }>({
      path: `/raffles/me`,
      method: 'GET',
    })
  }

  public async getMe() {
    return await this.fetch<{
      me: { profile: { referralCode: string }; boost: { amount: number; expiresAt: string | null } | null, email?: string }
    }>({
      path: `/users/me`,
      method: 'GET',
    })
  }

  public async getBadges() {
    return await this.fetch<{
      badges: { id: BADGE_ID }[]
    }>({
      path: `/badges/me`,
      method: 'GET',
    })
  }

  public async getMintBadgeSignature({ badgeId }: { badgeId: BADGE_ID }) {
    return await this.fetch<{
      signature: `0x${string}`
    }>({
      path: `/badges/mint/signature`,
      method: 'POST',
      body: {
        badgeId: badgeId,
      },
    })
  }

  public async getMyReferrals() {
    return await this.fetch<ReferralDataResponse>({
      path: `/users/me/referrals`,
      method: 'GET',
    })
  }

  public async getRaffles() {
    return await this.fetch<{ raffles: Raffle[] }>({
      path: `/raffles`,
      method: 'GET',
    })
  }

  public async getMyGachaElementsV2() {
    return await this.fetch<{ elements: Record<number, number> }>({
      path: `/gacha-game/me/elements-v2`,
      method: 'GET',
    })
  }

  public async buyRaffleTickets({ raffleId, count, userData }: {
    raffleId: string;
    count: number;
    userData?: Record<string, string>
  }) {
    return await this.fetch<{ success: true; newTotalPower: string } | {
      success: false;
      error: string;
      errorCode?: RAFFLE_ERROR_CODE
    }>({
      path: `/raffles/${raffleId}/buy-tickets`,
      method: 'POST',
      body: { count, userData },
    })
  }

  public async getLingoPrice() {
    return await this.fetch<{ price: number; changePercentage24h: number }>({
      path: '/coin/price',
      method: 'GET',
    })
  }

  public async getConfig() {
    return await this.fetch<LockConfig>({
      path: '/staking/config',
      method: 'GET',
    })
  }

  public async getSnapshotV2() {
    return await this.fetch<{ snapshot: Snapshot | null }>({
      path: '/staking/snapshot',
      method: 'GET',
    })
  }

  public async getQuote(amount: number, currency: string, convertedCurrency: string, blockchain: string, quoteCurrency: string) {
    return await this.fetch<{ data: { payment_method: string; converted_amount: string }[] }>({
      path: '/ramp/buy/quote',
      method: 'POST',
      body: {
        amount,
        currency,
        converted_currency: convertedCurrency,
        blockchain,
        quote_currency: quoteCurrency,
      },
    })
  }

  public async checkTransaction(txHash: string) {
    await this.fetch<{ success: boolean }>({
      path: '/staking/check-tx',
      method: 'POST',
      body: {
        txHash,
      },
    })
  }

  public async checkTransactions(transactionHashes: string[]) {
    await this.fetch<{ success: boolean }>({
      path: '/staking/check-transactions',
      method: 'POST',
      body: {
        transactionHashes,
      },
    })
  }

  public async getIslandCampaignSignature() {
    return await this.fetch<{ signature: `0x${string}` | null; amount: number }>({
      path: '/token/launch-island-campaign/signature',
      method: 'POST',
    })
  }

  public async getIslandCampaignAmount() {
    return await this.fetch<{ amounts: { firstClass: number; dream: number } }>({
      path: '/token/launch-island-campaign/tokens',
      method: 'GET',
    })
  }

  public async lastClaims() {
    return await this.fetch<Record<Beneficiary, number>>({
      path: '/token/last-claims',
      method: 'GET',
    })
  }

  public async claimedLingo(beneficiary: Beneficiary) {
    await this.fetch({
      path: '/token/claimed',
      method: 'POST',
      body: {
        beneficiary,
      },
    })
  }

  public async smashAsteroid() {
    return await this.fetch<{ success: false } | { success: true; prize: GachaAsteroidPrize }>({
      path: '/gacha-game/smash-asteroid',
      method: 'POST',
    })
  }

  public async getUserActivities({ pageSize, types, pageToken }: {
    pageSize: number;
    types: ACTIVITY_TYPE[];
    pageToken?: string
  }) {
    const response = await this.fetch<{ items: Activity<string>[]; meta: { nextPageToken: string | null } }>({
      path: '/users/me/activities',
      method: 'POST',
      body: {
        pageSize,
        types,
        pageToken: pageToken ?? null,
      },
    })

    const updatedResponse = response.items.map((item) => ({
      ...item,
      timestamp: new Date(item.timestamp),
    }))

    return {
      items: updatedResponse,
      meta: response.meta,
    } satisfies {
      items: Activity[]
      meta: { nextPageToken: string | null }
    }
  }

  public async updateUserEmail(email: string) {
    return await this.fetch<{ success: boolean }>({
      path: '/users/me/email',
      method: 'POST',
      body: {
        email: email,
      },
    })
  }

  public async getRaffleWinners(raffleId: string) {
    return await this.fetch<{ winners: string[] }>({
      path: `/raffles/${raffleId}/winners`,
      method: 'GET',
    })
  }

  public async getRaffleWinnersV2(raffleId: string) {
    return await this.fetch<WinnersResponse>({
      path: `/raffles/${raffleId}/winners-v2`,
      method: 'GET',
    })
  }

  public async getRecentClaims() {
    return await this.fetch<{ recentClaims: RecentClaim[] }>({
      path: '/raffles/recent-claims-v2',
      method: 'GET',
    })
  }

  public async getExclusiveEvents() {
    return await this.fetch<{ exclusiveEvents: ExclusiveEvent[] }>({
      path: '/exclusive-events',
      method: 'GET',
    })
  }

  public async claimExclusiveEvent({ eventId }: { eventId: string }) {
    return await this.fetch<{ success: true } | { success: false, error: string }>({
      path: `/exclusive-events/${eventId}/claim`,
      method: 'POST',
      body: {
        eventId,
      },
    })
  }

  public async getUserExclusiveClaimedEvents() {
    return await this.fetch<{ claims: ExclusiveEventClaim[] }>({
      path: '/exclusive-events/me',
      method: 'GET',
    })
  }

  public async getExclusiveClaimedEventClaimers({ eventId }: { eventId: string }) {
    return await this.fetch<{ claimers: ExclusiveEventClaim[] }>({
      path: `/exclusive-events/${eventId}/claimers`,
      method: 'GET',
    })
  }
}

export default new Api()
